import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import type Immutable from 'immutable';
import { pipe, Option, Array as A } from 'effect';

import { formatAmount } from '@/lib/formatAmount';

type SelectedPaymentRow = {
  vendorName?: string;
  currency?: string;
  totalAmount?: string | number;
};

const useConfirmOptions = (paymentsIds: Immutable.Set<string>, selectedPayments: Immutable.Map<string, any>) => {
  const { formatMessage } = useIntl();

  const mergePaymentsData = useMemo(() => {
    const items: ReadonlyArray<SelectedPaymentRow | undefined> = selectedPayments.valueSeq().toArray();
    const summCents = A.reduce(
      items,
      0,
      (acc, item) => acc + (Math.round(parseFloat(String(item?.totalAmount)) * 100) || 0),
    );
    const currencies = pipe(
      items,
      A.map((item) => item?.currency),
      A.filter((code): code is string => Boolean(code)),
      A.dedupe,
    );
    // a mixed-currency selection gets no symbol; its codes are listed in the text
    const currencyCode = currencies.length === 1 ? currencies[0] : null;
    const totalAmount = summCents / 100;
    return {
      vendorName: pipe(
        A.last(items),
        Option.map((item) => item?.vendorName ?? ''),
        Option.getOrElse(() => ''),
      ),
      currency: currencies.join(', '),
      count: paymentsIds.size,
      totalAmount: formatAmount(totalAmount),
      totalAmountWithCurrency: formatAmount(totalAmount, currencyCode),
    };
  }, [paymentsIds.size, selectedPayments]);

  return useMemo(
    () => ({
      mergePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.title',
          defaultMessage: 'Merge payments?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.placeholder',
            defaultMessage:
              '{count} payment(s) from {vendorName} in {currency} will be merged into one payment of {totalAmount}.',
          },
          // merging takes a single currency, so the total carries its symbol
          { ...mergePaymentsData, totalAmount: mergePaymentsData.totalAmountWithCurrency },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.mergePayments.btn',
          defaultMessage: 'Merge',
        }),
      },
      canNotMergePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.canNotMergePayments.title',
          defaultMessage: "Can't merge these payments",
        }),
        description: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.canNotMergePayments.placeholder',
          defaultMessage: 'To merge payments, select items from the same vendor and currency.',
        }),
        confirmationText: formatMessage({
          id: 'button.ok',
          defaultMessage: 'Ok',
        }),
        hideCancelButton: true,
      },
      sendToApproval: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.title',
          defaultMessage: 'Send to approval?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be send to approval.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.sendToApproval.btn',
          defaultMessage: 'Send to approval',
        }),
      },
      startPaymentProcess: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.title',
          defaultMessage: 'Start payment process?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.placeholder',
            defaultMessage: `The payment process for ${paymentsIds.size} payment(s) will start.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.startPaymentProcess.btn',
          defaultMessage: 'Start payment process',
        }),
      },
      saveForLater: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.title',
          defaultMessage: 'Save for later?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be saved for later.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.saveForLater.btn',
          defaultMessage: 'Save for later',
        }),
      },
      archive: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.title',
          defaultMessage: 'Archive?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be archived.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.archive.btn',
          defaultMessage: 'Archive',
        }),
      },
      unarchive: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.title',
          defaultMessage: 'Unarchive?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be unarchived.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.unarchive.btn',
          defaultMessage: 'Unarchive',
        }),
      },
      approve: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.title',
          defaultMessage: 'Approve?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be approved.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.approve.btn',
          defaultMessage: 'Approve',
        }),
      },
      reject: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.title',
          defaultMessage: 'Reject?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.placeholder',
            defaultMessage: `${paymentsIds.size} payment(s) will be rejected.`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.reject.btn',
          defaultMessage: 'Reject',
        }),
      },
      resetApprovalFlow: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.title',
          defaultMessage: 'Reset approval flow?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.placeholder',
            defaultMessage: `The approval flow will be reset for ${paymentsIds.size} payment(s).`,
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.resetApprovalFlow.btn',
          defaultMessage: 'Reset',
        }),
      },
      pay: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.title',
          defaultMessage: 'Pay?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.placeholder',
            defaultMessage: '{count} payment(s) will be paid for a total of {totalAmount} {currency}.',
          },
          { count: paymentsIds.size, totalAmount: mergePaymentsData.totalAmount, currency: mergePaymentsData.currency },
        ),
        confirmationText: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.pay.btn',
            defaultMessage: 'Pay {totalAmount}',
          },
          { totalAmount: mergePaymentsData.totalAmountWithCurrency },
        ),
      },
      deletePayments: {
        title: formatMessage({
          id: 'company.paymentsWorkspace.bulkPanel.confirm.deletePayments.title',
          defaultMessage: 'Delete payments?',
        }),
        description: formatMessage(
          {
            id: 'company.paymentsWorkspace.bulkPanel.confirm.deletePayments.placeholder',
            defaultMessage: '{count} payment(s) will be deleted.',
          },
          { count: paymentsIds.size },
        ),
        confirmationText: formatMessage({
          id: 'button.delete',
          defaultMessage: 'Delete',
        }),
        confirmationButtonProps: {
          color: 'error' as const,
        },
      },
    }),
    [formatMessage, mergePaymentsData, paymentsIds.size],
  );
};

export default useConfirmOptions;
